import type { CallAudioDownloadManifest } from "@t3tools/contracts";

export type CallAudioDownloadProgress = {
  completed: number;
  failed: number;
  total: number;
};

const AUDIO_CONCURRENCY = 4;

type DirectoryPickerWindow = Window & {
  showDirectoryPicker?: (options?: {
    mode?: "read" | "readwrite";
  }) => Promise<FileSystemDirectoryHandle>;
};

export async function chooseCallAudioDownloadDirectory(): Promise<FileSystemDirectoryHandle> {
  const picker = (window as DirectoryPickerWindow).showDirectoryPicker;
  if (!picker) {
    throw new Error("Choose a folder from Chrome to save this download.");
  }
  return picker.call(window, { mode: "readwrite" });
}

export function callAudioDownloadPickerCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * Writes manifest text immediately and audio as each response arrives. One
 * file that fails is recorded; the other files still save.
 */
export async function writeCallAudioDownload(input: {
  directory: FileSystemDirectoryHandle;
  manifest: CallAudioDownloadManifest;
  onProgress?: (progress: CallAudioDownloadProgress) => void;
}): Promise<{ failed: number }> {
  const clientFailures: { message: string; name: string }[] = [];
  let completed = 0;
  let failed = input.manifest.failures.length;
  const total = input.manifest.files.length;
  const report = () => input.onProgress?.({ completed, failed, total });
  report();

  const textFiles = input.manifest.files.filter((file) => file.text != null);
  const remoteFiles = input.manifest.files.filter((file) => file.text == null && file.url);
  const incomplete = input.manifest.files.filter((file) => file.text == null && !file.url);

  for (const file of textFiles) {
    try {
      await writeText(input.directory, file.name, file.text ?? "");
      completed += 1;
    } catch {
      failed += 1;
      clientFailures.push({ message: "This file could not be saved.", name: file.name });
    }
    report();
  }
  for (const file of incomplete) {
    failed += 1;
    clientFailures.push({ message: "This file could not be saved.", name: file.name });
    report();
  }

  await mapPool(remoteFiles, AUDIO_CONCURRENCY, async (file) => {
    try {
      await writeRemote(input.directory, file.name, file.url ?? "");
      completed += 1;
    } catch {
      failed += 1;
      clientFailures.push({ message: "This file could not be saved.", name: file.name });
    }
    report();
  });

  const recorded = [...input.manifest.failures, ...clientFailures];
  if (recorded.length > 0) {
    await writeText(
      input.directory,
      "errors.txt",
      `${recorded.map((failure) => `${failure.name}: ${failure.message}`).join("\n")}\n`,
    );
  }
  return { failed };
}

async function mapPool<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;
  let index = 0;
  const run = async () => {
    for (;;) {
      const current = index;
      index += 1;
      const item = items[current];
      if (item === undefined) return;
      await worker(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => run()));
}

function pathSegments(path: string): string[] {
  const parts = path.split("/");
  if (
    parts.length === 0 ||
    parts.some((part) => part.length === 0 || part === "." || part === ".." || part.includes("\\"))
  ) {
    throw new Error("Invalid download path.");
  }
  return parts;
}

async function fileHandle(
  directory: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemFileHandle> {
  const parts = pathSegments(path);
  let current = directory;
  for (const part of parts.slice(0, -1)) {
    current = await current.getDirectoryHandle(part, { create: true });
  }
  const name = parts[parts.length - 1];
  if (!name) throw new Error("Invalid download path.");
  return current.getFileHandle(name, { create: true });
}

async function writeText(
  directory: FileSystemDirectoryHandle,
  path: string,
  text: string,
): Promise<void> {
  const handle = await fileHandle(directory, path);
  const writable = await handle.createWritable();
  try {
    await writable.write(text);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
}

async function writeRemote(
  directory: FileSystemDirectoryHandle,
  path: string,
  url: string,
): Promise<void> {
  const response = await fetch(url, { credentials: "omit" });
  if (!response.ok || !response.body) {
    throw new Error("The audio file could not be downloaded.");
  }
  const handle = await fileHandle(directory, path);
  const writable = await handle.createWritable();
  try {
    const reader = response.body.getReader();
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      if (chunk.value) await writable.write(chunk.value);
    }
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
}
